import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import api, { PLATFORM_NAME, errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";

const Row = ({ label, value }) =>
  value ? (
    <div className="min-w-0">
      <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</p>
      <p className="mt-1 break-all text-sm font-semibold text-neutral-900">{value}</p>
    </div>
  ) : null;

export default function InvoiceView() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/invoices/${id}`).then(({ data }) => setData(data)).catch((e) => setError(errMsg(e)));
  }, [id]);

  if (error) return <p data-testid="invoice-view-error" className="text-sm text-red-400">{error}</p>;
  if (!data) return <p className="text-sm text-slate-500">Loading invoice…</p>;

  const { invoice: inv, manifest: m, company: c } = data;

  return (
    <div data-testid="invoice-view" className="space-y-6">
      <div className="no-print flex items-center justify-between gap-4">
        <Link to="/app/invoices" data-testid="back-to-invoices" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold">
          <ArrowLeft className="h-4 w-4" /> All invoices
        </Link>
        <Button data-testid="print-invoice-btn" variant="outline" className="border-ink-600 bg-transparent text-slate-300 hover:text-gold" onClick={() => window.print()}>
          <Printer className="mr-2 h-4 w-4" /> Print / PDF
        </Button>
      </div>

      <div className="print-sheet mx-auto max-w-3xl overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="px-8 py-7" style={{ background: c.branding?.accent_color || "#0F131C" }}>
          <div className="flex items-start justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em]" style={{ color: c.branding?.primary_color || "#D4AF37" }}>
                {c.name}
              </p>
              <p className="mt-2 font-display text-2xl font-bold text-white">INVOICE #{inv.number}</p>
              <p className="mt-1 text-xs text-neutral-400">{c.address} · {c.phone}</p>
            </div>
            {c.logo_url && <img src={c.logo_url} alt={`${c.name} logo`} className="h-14 w-auto object-contain" />}
          </div>
        </div>
        <div className="space-y-7 p-8">
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
            <Row label="Billed to" value={inv.customer_name} />
            <Row label="Email" value={inv.customer_email} />
            <Row label="Issued" value={String(inv.issued_at).slice(0, 10)} />
            <Row label="Due" value={String(inv.due_at).slice(0, 10)} />
          </div>
          {m && (
            <div className="grid grid-cols-2 gap-6 border-t border-neutral-200 pt-6 sm:grid-cols-4">
              <Row label="Manifest" value={`#${m.number}`} />
              <Row label="Trip date" value={m.trip_date} />
              <Row label="Vehicle" value={m.vehicle_name} />
              <Row label="Chauffeur" value={m.chauffeur_name} />
            </div>
          )}
          {m && (
            <div className="grid grid-cols-1 gap-6 border-t border-neutral-200 pt-6 sm:grid-cols-2">
              <Row label="Pickup" value={m.pickup?.address} />
              <Row label="Drop-off" value={m.dropoff?.address} />
            </div>
          )}
          <div className="flex flex-wrap items-end justify-between gap-6 border-t border-neutral-200 pt-6">
            <div className="flex gap-8">
              <Row label="Paid at reservation" value={money(inv.deposit_due)} />
              <Row label="Balance" value={money(inv.balance_due)} />
              <Row label="Status" value={inv.status} />
            </div>
            <div className="text-right">
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Invoice total</p>
              <p data-testid="invoice-total" className="font-display text-3xl font-bold text-neutral-900">{money(inv.amount)}</p>
            </div>
          </div>
          <div className="border-t border-neutral-200 pt-5">
            <p className="text-[10px] leading-relaxed text-neutral-500">
              <strong>Transportation Service Disclaimer</strong> — This transportation service is provided by{" "}
              {c.name}, the transportation operator identified on this reservation. The software platform
              facilitates communication, scheduling, dispatch and payment technology but does not provide or
              operate the transportation service.
            </p>
            {c.show_powered_by && (
              <p data-testid="invoice-powered-by" className="mt-3 font-mono text-[10px] text-neutral-400">Powered by {PLATFORM_NAME}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
